import { ThemeInjector } from "./ThemeInjector";
import type { RouteUtil, PageType, ParsedRoute } from "./RouteUtil";
import { createDebug } from "../utils/LazyDebug";

const debug = createDebug("wk:theme");

export interface WatchTarget {
  selector: string;
  // firstMatch?: boolean;
  // skipHidden?: boolean;
  lazy?: boolean;
  pageType?: WatchablePageType | WatchablePageType[];
}

export type WatchablePageType = PageType | "all";

export interface Subscriber {
  target: WatchTarget;
  callback: Callback;
  observer?: IntersectionObserver;
}

export type Callback = (injector: ThemeInjector) => void;

export type SubscriberList = Array<Subscriber>;

export class ThemeObserver {
  #observer: MutationObserver;
  #subscribers: SubscriberList;
  #routeUtil: RouteUtil;

  constructor({ routeUtil }: { routeUtil: RouteUtil }) {
    this.#subscribers = [];
    this.#routeUtil = routeUtil;

    this.#observer = new MutationObserver((mutations) => {
      mutations
        .reduce(
          (acc, mutation) => acc.concat(Array.from(mutation.addedNodes)),
          [] as Node[],
        )
        .filter(
          (node) => node instanceof Element && !node.hasAttribute("wk-skip"),
        )
        .forEach((node) => {
          if (node instanceof Element) {
            this.scan(node);
          }
        });
    });

    this.#observer.observe(document, {
      subtree: true,
      childList: true,
      attributes: false,
    });
  }

  isNearViewport(element: Element) {
    const bounds = element.getBoundingClientRect();
    const viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;

    const topMargin = -viewportHeight;
    const bottomMargin = 2 * viewportHeight;
    const leftMargin = -viewportWidth;
    const rightMargin = 2 * viewportWidth;

    // debug("Is near viewport", {
    //   element,
    //   bounds,
    // });

    return (
      ((bounds.top > topMargin && bounds.top < viewportHeight) ||
        (bounds.bottom > bottomMargin && bounds.bottom < viewportHeight)) &&
      ((bounds.left > leftMargin && bounds.left < viewportWidth) ||
        (bounds.right > rightMargin && bounds.right < viewportWidth))
    );
  }

  watch(target: WatchTarget, callback: Callback) {
    debug("Watch theme for target", { target });

    const subscriber = {
      target,
      callback,
    };

    this.#subscribers.push(subscriber);

    if (
      isRouteSubscriber(
        subscriber,
        this.#routeUtil.parse(window.location.pathname),
      )
    ) {
      this.scan(document);
    }
  }

  scan(rootElement: Element | Document) {
    if (!rootElement) {
      return;
    }
    this.#subscribers
      .filter((subscriber) =>
        isRouteSubscriber(
          subscriber,
          this.#routeUtil.parse(window.location.pathname),
        ),
      )
      .forEach((subscriber) => {
        const isRootMatch =
          rootElement instanceof Element &&
          rootElement.matches(subscriber.target.selector);

        if (isRootMatch) {
          this.notify(subscriber, rootElement);
        }

        const childMatches = rootElement.querySelectorAll(
          subscriber.target.selector,
        );

        for (const element of childMatches) {
          this.notify(subscriber, element);
        }
      });
  }

  notify(subscriber: Subscriber, element: Element) {
    if (!element.hasAttribute("wk-skip")) {
      element.setAttribute("wk-skip", "");

      debug("Found watched element", {
        target: subscriber.target,
        element,
        lazy: subscriber.target.lazy,
        isNearViewport: this.isNearViewport(element),
      });

      if (subscriber.target.lazy && !this.isNearViewport(element)) {
        debug("Lazy target not in viewport", {
          target: subscriber.target,
          element,
        });
        new IntersectionObserver(
          function ([entry]: IntersectionObserverEntry[]) {
            if (entry.isIntersecting) {
              debug("Lazy target entered viewport, notify subscriber", {
                target: subscriber.target,
                element,
              });
              // @ts-ignore
              this.disconnect();
              subscriber.callback(new ThemeInjector(element as HTMLElement));
            }
          },
          {
            root: null,
            rootMargin: "100% 100% 100% 100%",
          },
        ).observe(element);
      } else {
        debug("Notify subscriber about watched element", {
          target: subscriber.target,
          element,
        });
        subscriber.callback(new ThemeInjector(element as HTMLElement));
      }
    }
  }
}

export function isRouteSubscriber(
  { target }: Subscriber,
  route: ParsedRoute,
): boolean {
  if (!target.pageType) {
    return true;
  }

  if (!route.pageType) {
    return false;
  }

  const pageTypes = Array.isArray(target.pageType)
    ? target.pageType
    : [target.pageType];

  if (pageTypes.includes("all")) {
    return true;
  }

  return pageTypes.includes(route.pageType);
}
